/**
 * EmitirNotaCxPModal — modal unificado para emitir una Nota de Crédito o Débito
 * sobre una factura CxP. Espejo de EmitirNotaModal (CxC).
 *   · CRÉDITO → reduce la deuda con el proveedor (tope = saldo disponible).
 *   · DÉBITO  → aumenta la deuda con el proveedor (cargo nuevo, sin tope).
 *
 * Reemplaza a EmitirNotaCreditoCxPModal + EmitirNotaDebitoCxPModal.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { calcularIva } from "../../shared/iva";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const OVERLAY = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  zIndex: 1000, backdropFilter: "blur(3px)",
};
const MODAL_BASE = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: "24px 24px 20px",
  width: "min(560px, 94vw)", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)", maxHeight: "90vh", overflowY: "auto",
};
const LABEL_STYLE = {
  display: "block", color: "var(--text-muted)", fontSize: "0.7rem",
  fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
};
const INPUT_STYLE = {
  width: "100%", padding: "8px 10px", background: "var(--bg-input)",
  border: "1px solid var(--border-card)", borderRadius: 6,
  color: "var(--text-primary)", fontSize: "0.85rem", outline: "none", colorScheme: "light dark",
};

const CONFIG = {
  CREDITO: {
    accent: "#a78bfa",
    accentSoft: "rgba(167,139,250,0.15)",
    icon: "fa-file-circle-minus",
    ns: "notasCredito",
    endpoint: "/api/finance/notaCreditoCxP/",
    bodyKey: "factura_cxp",
    fetchPath: (id) => `/api/finance/cxp/${id}/notas-credito/`,
    saldoKey: "saldo_disponible",
    notasKey: "notas_credito",
    saldoLabel: "notasCredito.saldoDisponible",
    totalLabel: "notasCredito.totalAcreditado",
    motivos: [
      { valor: "DEVOLUCION", labelKey: "notasCredito.motivoDevolucion" },
      { valor: "CORRECCION", labelKey: "notasCredito.motivoCorreccion" },
      { valor: "DESCUENTO", labelKey: "notasCredito.motivoDescuento" },
      { valor: "ANULACION", labelKey: "notasCredito.motivoAnulacion" },
      { valor: "OTRO", labelKey: "notasCredito.motivoOtro" },
    ],
    tieneTope: true,
  },
  DEBITO: {
    accent: "#fb923c",
    accentSoft: "rgba(251,146,60,0.15)",
    icon: "fa-file-circle-plus",
    ns: "notasDebito",
    endpoint: "/api/finance/notaDebitoCxP/",
    bodyKey: "factura_cxp",
    fetchPath: (id) => `/api/finance/cxp/${id}/notas-debito/`,
    saldoKey: "saldo_actual",
    notasKey: "notas_debito",
    saldoLabel: "notasDebito.saldoActual",
    totalLabel: "notasDebito.totalCargado",
    motivos: [
      { valor: "INTERESES", labelKey: "notasDebito.motivoIntereses" },
      { valor: "RECARGO", labelKey: "notasDebito.motivoRecargo" },
      { valor: "CORRECCION", labelKey: "notasDebito.motivoCorreccion" },
      { valor: "CARGO", labelKey: "notasDebito.motivoCargo" },
      { valor: "OTRO", labelKey: "notasDebito.motivoOtro" },
    ],
    tieneTope: false,
  },
};

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function EmitirNotaCxPModal({ factura, onClose, onSuccess, tipoInicial = "CREDITO" }) {
  const { t } = useTranslation();
  const facturaId = factura.id;
  const moneda = (factura.moneda || "CRC").toUpperCase();
  const simbolo = moneda === "USD" ? "$" : "₡";

  const [tipo, setTipo] = useState(tipoInicial);
  const cfg = CONFIG[tipo];

  const [loadingSaldo, setLoadingSaldo] = useState(true);
  const [saldo, setSaldo] = useState(0);
  const [notasPrevias, setNotasPrevias] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    motivo: CONFIG[tipoInicial].motivos[0].valor,
    concepto: "",
    monto: "",
    fecha: todayISO(),
  });

  useEffect(() => {
    let cancelled = false;
    setLoadingSaldo(true);
    setError("");
    setForm((prev) => ({ ...prev, motivo: cfg.motivos[0].valor, monto: "" }));
    apiFetch(cfg.fetchPath(facturaId))
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setSaldo(Number(data[cfg.saldoKey]) || 0);
        setNotasPrevias(data[cfg.notasKey] ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingSaldo(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, facturaId]);

  function setField(k, v) {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (tipo === "CREDITO" && k === "motivo" && v === "ANULACION" && saldo > 0) {
        next.monto = String(saldo);
      }
      return next;
    });
  }

  const montoNum = Number(form.monto) || 0;
  const montoValido = cfg.tieneTope ? (montoNum > 0 && montoNum <= saldo) : montoNum > 0;
  const conceptoValido = form.concepto.trim().length > 0;
  const { base, iva, total } = calcularIva(montoNum, factura.tasa_iva, factura.iva_incluido, "INCLUIDO");

  async function handleSubmit() {
    if (!montoValido || !conceptoValido || submitting) return;
    setSubmitting(true);
    setError("");
    const res = await apiFetch(cfg.endpoint, {
      method: "POST",
      body: JSON.stringify({
        [cfg.bodyKey]: facturaId,
        fecha: form.fecha,
        motivo: form.motivo,
        concepto: form.concepto.trim(),
        monto: montoNum,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      onSuccess?.({ ...data, __tipo: tipo });
      onClose();
    } else {
      const msg =
        data.monto?.[0] ?? data.detail ?? data.non_field_errors?.[0] ?? t(`${cfg.ns}.errorEmitir`);
      setError(msg);
    }
    setSubmitting(false);
  }

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={MODAL_BASE}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: cfg.accentSoft, borderRadius: 8, padding: "8px 10px", color: cfg.accent }}>
              <i className={`fa-solid ${cfg.icon}`} style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                {t("notas.emitir")}
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                {factura.numero_doc_proveedor || factura.numero_factura}
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>

        {/* Selector de tipo Crédito / Débito */}
        <div>
          <label style={LABEL_STYLE}>{t("notas.tipo")}</label>
          <div style={{
            display: "flex", borderRadius: 9,
            border: "1px solid var(--border-card)", overflow: "hidden", marginBottom: 4,
          }}>
            {[
              { key: "CREDITO", label: t("notas.credito"), icon: "fa-file-circle-minus" },
              { key: "DEBITO", label: t("notas.debito"), icon: "fa-file-circle-plus" },
            ].map((opt, i) => {
              const active = tipo === opt.key;
              const c = CONFIG[opt.key];
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setTipo(opt.key)}
                  disabled={submitting}
                  style={{
                    flex: 1, padding: "10px 0", border: "none",
                    borderRight: i === 0 ? "1px solid var(--border-card)" : "none",
                    background: active ? c.accentSoft : "transparent",
                    color: active ? c.accent : "var(--text-muted)",
                    fontWeight: active ? 700 : 500, fontSize: "0.88rem",
                    cursor: submitting ? "not-allowed" : "pointer", transition: "all 0.15s",
                  }}
                >
                  <i className={`fa-solid ${opt.icon}`} style={{ marginRight: 7 }} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <Divider />

        {/* Saldo */}
        {loadingSaldo ? (
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", padding: "8px 0" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />
            {t(`${cfg.ns}.cargandoSaldo`)}
          </p>
        ) : (
          <WarningBox icon="fa-circle-info" color="#60a5fa">
            {t(cfg.saldoLabel)}: {simbolo}{formatCurrency(saldo)}
          </WarningBox>
        )}

        {/* Notas previas */}
        {!loadingSaldo && notasPrevias.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_STYLE}>{t(`${cfg.ns}.notasPrevias`)}</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {notasPrevias.map((n) => (
                <div key={n.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "var(--bg-input)", border: "1px solid var(--border-card)",
                  borderRadius: 6, padding: "6px 10px", fontSize: "0.78rem",
                }}>
                  <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>{n.numero_nota}</span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {fmtDate(n.fecha)} · {t(MOTIVO_LABEL(cfg, n.motivo))}
                  </span>
                  <span style={{ color: cfg.accent, fontWeight: 600 }}>
                    {simbolo}{formatCurrency(n.monto)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formulario */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 16px" }}>
          <div>
            <label style={LABEL_STYLE}>{t(`${cfg.ns}.motivo`)}</label>
            <select style={{ ...INPUT_STYLE, cursor: "pointer" }} value={form.motivo}
              onChange={(e) => setField("motivo", e.target.value)}>
              {cfg.motivos.map((m) => (
                <option key={m.valor} value={m.valor}>{t(m.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={LABEL_STYLE}>{t(`${cfg.ns}.fecha`)}</label>
            <input type="date" style={INPUT_STYLE} value={form.fecha}
              onChange={(e) => setField("fecha", e.target.value)} />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t(`${cfg.ns}.concepto`)}</label>
            <textarea style={{ ...INPUT_STYLE, minHeight: 64, resize: "vertical" }}
              value={form.concepto} placeholder={t(`${cfg.ns}.conceptoPlaceholder`)}
              onChange={(e) => setField("concepto", e.target.value)} />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>{t(`${cfg.ns}.monto`)}</label>
            <input type="number" style={INPUT_STYLE} value={form.monto} min="0" step="0.01"
              onChange={(e) => setField("monto", e.target.value)} />
            {cfg.tieneTope && montoNum > 0 && !montoValido && (
              <p style={{ margin: "4px 0 0", fontSize: "0.75rem", color: "#f87171" }}>
                {t("notasCredito.montoSuperaSaldo", { saldo: `${simbolo}${formatCurrency(saldo)}` })}
              </p>
            )}
          </div>
        </div>

        {/* Preview de IVA */}
        {montoNum > 0 && (
          <div style={{
            marginTop: 14, background: "rgba(0,0,0,0.15)", borderRadius: 7,
            padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6,
          }}>
            <Row label={t(`${cfg.ns}.baseGravable`)} value={`${simbolo}${formatCurrency(base)}`} accent={cfg.accent} />
            <Row
              label={`${t(`${cfg.ns}.iva`)} (${factura.iva_incluido ? Number(factura.tasa_iva) : 0}%)`}
              value={`${simbolo}${formatCurrency(iva)}`} accent={cfg.accent}
            />
            <div style={{ height: 1, background: "var(--border-card)", margin: "2px 0" }} />
            <Row label={t(cfg.totalLabel)} value={`${simbolo}${formatCurrency(total)}`} strong accent={cfg.accent} />
          </div>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <CancelBtn onClick={onClose} label={t("common.cancelar")} />
          <button
            onClick={handleSubmit}
            disabled={!montoValido || !conceptoValido || submitting}
            style={{
              padding: "8px 18px",
              background: !montoValido || !conceptoValido || submitting
                ? cfg.accentSoft : cfg.accent,
              color: "#fff", border: "none", borderRadius: 7,
              fontSize: "0.84rem", fontWeight: 600,
              cursor: !montoValido || !conceptoValido || submitting ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}
          >
            {submitting ? (
              <><i className="fa-solid fa-circle-notch fa-spin" /> {t("common.guardando")}</>
            ) : (
              <><i className={`fa-solid ${cfg.icon}`} /> {t("notas.emitir")}</>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function MOTIVO_LABEL(cfg, motivo) {
  const found = cfg.motivos.find((m) => m.valor === motivo);
  return found ? found.labelKey : `${cfg.ns}.motivoOtro`;
}

// ─────────────── Helpers de UI ───────────────
function Divider() {
  return <div style={{ borderTop: "1px solid var(--border-card)", margin: "16px 0" }} />;
}
function CloseBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "none", border: "none", color: "var(--text-muted)",
      cursor: "pointer", fontSize: "1.15rem", lineHeight: 1, padding: "2px 4px",
    }}>✕</button>
  );
}
function CancelBtn({ onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 16px", background: "transparent",
      border: "1px solid var(--border-card)", borderRadius: 7,
      color: "var(--text-muted)", fontSize: "0.84rem", cursor: "pointer",
    }}>{label}</button>
  );
}
function WarningBox({ children, icon, color }) {
  return (
    <div style={{
      background: `${color}18`, border: `1px solid ${color}44`, borderLeft: `3px solid ${color}`,
      borderRadius: 6, padding: "9px 12px", color, fontSize: "0.83rem",
      marginBottom: 12, display: "flex", gap: 8, alignItems: "flex-start",
    }}>
      <i className={`fa-solid ${icon}`} style={{ marginTop: 2, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}
function ErrorBox({ children }) {
  return (
    <div style={{
      background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
      borderLeft: "3px solid #f87171", borderRadius: 6, padding: "8px 12px",
      color: "#fca5a5", fontSize: "0.82rem", marginTop: 10,
    }}>
      <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }} />
      {children}
    </div>
  );
}
function Row({ label, value, strong, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{
        fontSize: strong ? "0.85rem" : "0.78rem",
        color: strong ? "var(--text-primary)" : "var(--text-muted)",
        fontWeight: strong ? 700 : 400,
      }}>{label}</span>
      <span style={{
        fontFamily: "monospace", fontWeight: strong ? 700 : 600,
        fontSize: strong ? "0.92rem" : "0.85rem",
        color: strong ? accent : "var(--text-secondary)",
      }}>{value}</span>
    </div>
  );
}
